import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '../../lib/errors'
import { formatMoney, formatPercentage } from '../../lib/format'
import { parseAmount } from '../../lib/number'
import { calculateCommission, type CommissionBreakdown } from './api'

// Mirrors the CalculateCommission slice on the API: same inputs, same shape back.
export function CommissionCalculatorPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? 'en'

  const [loadTotal, setLoadTotal] = useState('1850')
  const [accessorials, setAccessorials] = useState('150')
  const [rate, setRate] = useState('10')

  const [breakdown, setBreakdown] = useState<CommissionBreakdown | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const amounts = {
      loadTotal: parseAmount(loadTotal),
      accessorials: parseAmount(accessorials),
      commissionPercentage: parseAmount(rate),
    }

    if (
      amounts.loadTotal === null ||
      amounts.accessorials === null ||
      amounts.commissionPercentage === null
    ) {
      setBreakdown(null)
      setError(t('commissions.invalidInput'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await calculateCommission({
        loadTotal: amounts.loadTotal,
        accessorials: amounts.accessorials,
        commissionPercentage: amounts.commissionPercentage,
      })
      setBreakdown(result)
    } catch (caught) {
      setBreakdown(null)
      // Same contract as every other slice: the API's English never reaches the screen.
      setError(apiErrorMessage(caught, t, 'commissions.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="page">
      <header className="page__header">
        <h1>{t('commissions.title')}</h1>
        <p className="page__subtitle">{t('commissions.subtitle')}</p>
      </header>

      <form className="form" onSubmit={(event) => void handleSubmit(event)}>
        <label className="field">
          <span>{t('commissions.loadTotal')}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={loadTotal}
            onChange={(event) => setLoadTotal(event.target.value)}
            // Focused number inputs change value on wheel scroll in Chrome — the
            // accidental edit Hugo hit on the TRKC-65 retest. Letting go of focus
            // disarms it; the page scrolls, the number stays.
            onWheel={(event) => event.currentTarget.blur()}
            required
          />
        </label>

        <label className="field">
          <span>{t('commissions.accessorials')}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={accessorials}
            onChange={(event) => setAccessorials(event.target.value)}
            onWheel={(event) => event.currentTarget.blur()}
            required
          />
        </label>

        <label className="field">
          <span>{t('commissions.rate')}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
            onWheel={(event) => event.currentTarget.blur()}
            required
          />
        </label>

        <button type="submit" className="button" disabled={isSubmitting}>
          {isSubmitting ? t('commissions.calculating') : t('commissions.calculate')}
        </button>
      </form>

      {error !== null && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {breakdown !== null && (
        <div className="result">
          <h2>{t('commissions.result')}</h2>
          <dl className="result__grid">
            <div>
              <dt>{t('commissions.billableTotal')}</dt>
              <dd>{formatMoney(breakdown.billableTotal, breakdown.currency, locale)}</dd>
            </div>
            <div>
              <dt>
                {t('commissions.commission')} ({formatPercentage(breakdown.commissionPercentage, locale)})
              </dt>
              <dd>{formatMoney(breakdown.commission, breakdown.currency, locale)}</dd>
            </div>
            <div>
              <dt>{t('commissions.netToCarrier')}</dt>
              <dd className="result__net">
                {formatMoney(breakdown.netToCarrier, breakdown.currency, locale)}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </section>
  )
}
